import { useEffect, useRef } from "react";
import { Viewport } from "./viewport";
import { drawGrid } from "./grid";
import { drawElement, drawSelectionBox } from "../render/drawElement";
import { imageCache } from "../render/imageCache";
import { DEFAULT_IMAGE_SIZE, sizeToMapUnits } from "../render/sizeCategories";
import { sceneStore } from "../store/sceneStore";
import { createToolRegistry } from "../tools/registry";
import type { Tool, ToolPointerEvent } from "../tools/tool";
import { ASSET_DRAG_MIME, type AssetDragPayload } from "../ui/AssetLibrary";
import { liveOverlay, colourForUser } from "../net/liveOverlay";
import { currentSceneClient } from "../net/currentClient";
import "./SceneCanvas.css";

/** Minimum gap between outgoing cursor broadcasts, ms. ~20 Hz. */
const CURSOR_SEND_INTERVAL_MS = 50;

/**
 * The VTT canvas.
 *
 * Two stacked canvases:
 *   - static — committed scene (grid + elements + selection outlines).
 *              Repaints when the store changes or the viewport moves.
 *   - live   — the active tool's preview (in-progress stroke, marquee,
 *              drag ghost). Repaints every animation frame while a tool
 *              interaction is active.
 *
 * Input routing: the live canvas sits on top, so all pointer events hit
 * it. The canvas translates screen → map coordinates once, builds a
 * `ToolPointerEvent`, and hands it to the active tool. Pan and zoom are
 * handled here (not by a tool) because they're global across every mode.
 *
 * The Viewport, the pan drag state, the active tool registry, and the
 * rAF handle all live in refs so pointer-move bursts don't force React
 * rerenders. The store is subscribed once to trigger a redraw whenever
 * elements or selection change.
 */
export default function SceneCanvas() {
  const hostRef = useRef<HTMLDivElement>(null);
  const staticCanvasRef = useRef<HTMLCanvasElement>(null);
  const liveCanvasRef = useRef<HTMLCanvasElement>(null);

  const viewportRef = useRef(new Viewport());
  const sizeRef = useRef({ cssW: 0, cssH: 0, dpr: 1 });
  const toolsRef = useRef(createToolRegistry());

  // Pan state — pan is a global navigation gesture, not a tool action.
  const panStateRef = useRef({
    active: false,
    pointerId: -1,
    lastX: 0,
    lastY: 0,
  });

  // Render scheduling.
  const staticDirtyRef = useRef(true);
  const rafRef = useRef<number | null>(null);
  /**
   * Last scene version we repainted at. The store bumps this only when
   * elements, selection, or layer visibility change — tool / tool-option
   * clicks leave it alone. That lets us skip full static repaints on
   * every palette click.
   */
  const lastSceneVersionRef = useRef(-1);

  // Cursor broadcast throttle.
  const lastCursorSendRef = useRef(0);

  useEffect(() => {
    const host = hostRef.current!;
    const staticC = staticCanvasRef.current!;
    const liveC = liveCanvasRef.current!;

    const getActiveTool = (): Tool => toolsRef.current[sceneStore.getState().tool];

    // ---------- sizing ----------

    const resize = () => {
      const rect = host.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const cssW = Math.max(1, Math.floor(rect.width));
      const cssH = Math.max(1, Math.floor(rect.height));
      sizeRef.current = { cssW, cssH, dpr };
      for (const c of [staticC, liveC]) {
        c.width = Math.floor(cssW * dpr);
        c.height = Math.floor(cssH * dpr);
        c.style.width = `${cssW}px`;
        c.style.height = `${cssH}px`;
      }
      staticDirtyRef.current = true;
      scheduleFrame();
    };

    // ---------- coord conversion ----------

    const toMap = (clientX: number, clientY: number) => {
      const rect = liveC.getBoundingClientRect();
      return viewportRef.current.screenToMap(clientX - rect.left, clientY - rect.top);
    };

    const buildEvt = (e: PointerEvent): ToolPointerEvent => {
      const m = toMap(e.clientX, e.clientY);
      return {
        x: m.x,
        y: m.y,
        pressure: e.pressure || 0.5,
        button: e.button,
        shift: e.shiftKey,
        pointerId: e.pointerId,
      };
    };

    // ---------- rendering ----------

    const renderStatic = () => {
      const ctx = staticC.getContext("2d");
      if (!ctx) return;
      const { cssW, cssH, dpr } = sizeRef.current;
      const vp = viewportRef.current;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);
      ctx.fillStyle = "#0c0a14";
      ctx.fillRect(0, 0, cssW, cssH);

      // Grid is drawn in screen space, unaffected by the viewport transform.
      drawGrid(ctx, vp, cssW, cssH);

      // Switch to map space for elements.
      ctx.save();
      vp.applyTo(ctx);

      const state = sceneStore.getState();
      // Honour the layer-visibility filter — DMs use it to mute player
      // scribbles during a boss fight without deleting anything.
      const elements = sceneStore.listVisibleElements();
      for (const el of elements) drawElement(ctx, el);

      // Selection outlines on top of elements, still in map space. Skip
      // elements whose layer is currently hidden so a stale selection
      // doesn't paint a ghost outline on an empty region.
      for (const id of state.selectedIds) {
        const el = state.elements[id];
        if (el && !el.isDeleted && state.visibleLayers.has(el.layer)) {
          drawSelectionBox(ctx, el, vp.zoom);
        }
      }

      ctx.restore();

      if (elements.length === 0) {
        ctx.save();
        ctx.fillStyle = "rgba(255, 255, 255, 0.24)";
        ctx.font = "14px 'JetBrains Mono', monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
          "empty canvas — pick a tool, or pan with middle-mouse",
          cssW / 2,
          cssH / 2,
        );
        ctx.restore();
      }
    };

    const renderLive = () => {
      const ctx = liveC.getContext("2d");
      if (!ctx) return;
      const { cssW, cssH, dpr } = sizeRef.current;
      const vp = viewportRef.current;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      // --- active tool preview (map space) ---
      const tool = getActiveTool();
      if (tool.drawPreview && tool.isActive()) {
        ctx.save();
        vp.applyTo(ctx);
        tool.drawPreview(ctx, sceneStore);
        ctx.restore();
      }

      // --- remote in-progress strokes (map space) ---
      const strokes = liveOverlay.getStrokes();
      if (strokes.length > 0) {
        ctx.save();
        vp.applyTo(ctx);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        for (const s of strokes) {
          const pts = s.points;
          if (pts.length < 4) continue;
          ctx.beginPath();
          ctx.moveTo(pts[0], pts[1]);
          for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i], pts[i + 1]);
          ctx.strokeStyle = s.strokeColor ?? colourForUser(s.userId);
          // Live strokes render a touch thinner than the committed version
          // so the swap at pointer-up isn't jarring.
          ctx.lineWidth = (s.strokeWidth ?? 2) / vp.zoom;
          ctx.globalAlpha = 0.85;
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
        ctx.restore();
      }

      // --- remote cursors (screen space) ---
      const cursors = liveOverlay.getCursors();
      if (cursors.length > 0) {
        ctx.save();
        ctx.font = "11px 'JetBrains Mono', monospace";
        ctx.textBaseline = "top";
        for (const c of cursors) {
          const p = vp.mapToScreen(c.x, c.y);
          if (p.x < -40 || p.y < -40 || p.x > cssW + 40 || p.y > cssH + 40) continue;
          const colour = colourForUser(c.userId);
          // Dot.
          ctx.fillStyle = colour;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
          ctx.fill();
          // Outline for contrast on bright regions.
          ctx.strokeStyle = "rgba(12, 10, 20, 0.85)";
          ctx.lineWidth = 1;
          ctx.stroke();
          // Label.
          const label = c.username ?? c.userId.slice(0, 6);
          const metrics = ctx.measureText(label);
          const padX = 4;
          const padY = 2;
          const boxX = p.x + 8;
          const boxY = p.y + 8;
          const boxW = metrics.width + padX * 2;
          const boxH = 14 + padY * 2;
          ctx.fillStyle = "rgba(12, 10, 20, 0.82)";
          ctx.fillRect(boxX, boxY, boxW, boxH);
          ctx.fillStyle = colour;
          ctx.fillText(label, boxX + padX, boxY + padY);
        }
        ctx.restore();
      }
    };

    const scheduleFrame = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (staticDirtyRef.current) {
          renderStatic();
          staticDirtyRef.current = false;
        }
        renderLive();
      });
    };

    const markStaticDirty = () => {
      staticDirtyRef.current = true;
      scheduleFrame();
    };

    // ---------- pointer handling ----------

    const onPointerDown = (e: PointerEvent) => {
      // Pan on middle or right mouse — takes priority over tools.
      if (e.button === 1 || e.button === 2) {
        e.preventDefault();
        panStateRef.current = {
          active: true,
          pointerId: e.pointerId,
          lastX: e.clientX,
          lastY: e.clientY,
        };
        liveC.setPointerCapture(e.pointerId);
        return;
      }

      // Left click → active tool.
      const tool = getActiveTool();
      tool.onPointerDown(buildEvt(e), sceneStore);
      if (tool.isActive()) {
        liveC.setPointerCapture(e.pointerId);
        scheduleFrame();
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      const pan = panStateRef.current;
      if (pan.active && e.pointerId === pan.pointerId) {
        const dx = e.clientX - pan.lastX;
        const dy = e.clientY - pan.lastY;
        pan.lastX = e.clientX;
        pan.lastY = e.clientY;
        viewportRef.current.panByScreen(dx, dy);
        markStaticDirty();
        return;
      }

      // Throttled cursor broadcast. Runs regardless of which tool is
      // active so idle hover still shows where a player is looking.
      const now = performance.now();
      if (now - lastCursorSendRef.current >= CURSOR_SEND_INTERVAL_MS) {
        lastCursorSendRef.current = now;
        const client = currentSceneClient.instance;
        if (client) {
          const m = toMap(e.clientX, e.clientY);
          client.sendCursor(m.x, m.y);
        }
      }

      const tool = getActiveTool();
      if (tool.isActive()) {
        tool.onPointerMove(buildEvt(e), sceneStore);
        scheduleFrame();
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      const pan = panStateRef.current;
      if (pan.active && e.pointerId === pan.pointerId) {
        pan.active = false;
        pan.pointerId = -1;
        try {
          liveC.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        return;
      }

      const tool = getActiveTool();
      if (tool.isActive()) {
        tool.onPointerUp(buildEvt(e), sceneStore);
        try {
          liveC.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        // Tool just finished → static canvas needs a repaint to show the
        // newly-committed element.
        markStaticDirty();
      }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = liveC.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const factor = Math.exp(-e.deltaY * 0.0015);
      viewportRef.current.zoomAt(sx, sy, factor);
      markStaticDirty();
    };

    const onContextMenu = (e: MouseEvent) => e.preventDefault();

    // ---------- drag / drop from asset library ----------
    //
    // The library serialises an AssetDragPayload onto the dataTransfer
    // under a custom MIME so we can tell library drops apart from
    // arbitrary file drags. We intentionally do NOT handle OS-file drops
    // here — the user must upload via the library first so every image
    // goes through the validation + commit pipeline.
    const hasAssetPayload = (e: DragEvent): boolean => {
      return !!e.dataTransfer && Array.from(e.dataTransfer.types).includes(ASSET_DRAG_MIME);
    };

    const onDragOver = (e: DragEvent) => {
      if (!hasAssetPayload(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };

    const onDrop = (e: DragEvent) => {
      if (!hasAssetPayload(e)) return;
      e.preventDefault();
      const raw = e.dataTransfer?.getData(ASSET_DRAG_MIME);
      if (!raw) return;
      let payload: AssetDragPayload;
      try {
        payload = JSON.parse(raw) as AssetDragPayload;
      } catch {
        return;
      }

      // Make sure the renderer has the bytes ready — the library
      // already preloads but a drop from a just-uploaded asset may race.
      imageCache.preload(payload.fileId, payload.downloadUrl);

      // Convert drop point to map coords and centre the default-sized
      // footprint on it. Default is Medium (1 grid cell).
      const side = sizeToMapUnits(DEFAULT_IMAGE_SIZE);
      const m = toMap(e.clientX, e.clientY);
      const el = sceneStore.createElement("image", {
        x: m.x - side / 2,
        y: m.y - side / 2,
        width: side,
        height: side,
        fileId: payload.fileId,
        naturalWidth: payload.naturalWidth,
        naturalHeight: payload.naturalHeight,
      });
      sceneStore.upsertLocal(el);
      sceneStore.setSelected([el.id]);
      markStaticDirty();
    };

    // ---------- keyboard shortcuts ----------

    const WIDTH_LADDER = [1, 2, 4, 8];

    const nudgeSelection = (dx: number, dy: number) => {
      const state = sceneStore.getState();
      const ids = [...state.selectedIds];
      if (ids.length === 0) return;
      for (const id of ids) {
        const el = state.elements[id];
        if (!el || el.isDeleted) continue;
        const bumped = sceneStore.bumpLocal({ ...el, x: el.x + dx, y: el.y + dy });
        sceneStore.upsertLocal(bumped);
      }
    };

    const duplicateSelection = () => {
      const state = sceneStore.getState();
      const ids = [...state.selectedIds];
      if (ids.length === 0) return;
      const newIds: string[] = [];
      // Small diagonal offset so the copy doesn't stack exactly on top.
      const OFFSET = 16;
      for (const id of ids) {
        const src = state.elements[id];
        if (!src || src.isDeleted) continue;
        // Strip identity/version fields so createElement's fresh id +
        // stamp survive the spread. Everything else (geometry, style,
        // per-type fields) carries over from the source element.
        const { id: _srcId, version: _srcV, versionNonce: _srcN, ...rest } = src;
        void _srcId; void _srcV; void _srcN;
        const dup = sceneStore.createElement(src.type, {
          ...rest,
          x: src.x + OFFSET,
          y: src.y + OFFSET,
          // Stamp ownerId to the local user so the DM duplicating a
          // player-owned element doesn't silently keep foreign ownership.
          ownerId: state.userId,
        });
        sceneStore.upsertLocal(dup);
        newIds.push(dup.id);
      }
      if (newIds.length > 0) sceneStore.setSelected(newIds);
    };

    const selectAllVisible = () => {
      const ids = sceneStore.listVisibleElements().map((e) => e.id);
      sceneStore.setSelected(ids);
    };

    const adjustStrokeWidth = (delta: number) => {
      const current = sceneStore.getState().toolOptions.strokeWidth;
      let idx = WIDTH_LADDER.indexOf(current);
      if (idx < 0) idx = 1;
      idx = Math.max(0, Math.min(WIDTH_LADDER.length - 1, idx + delta));
      sceneStore.setToolOptions({ strokeWidth: WIDTH_LADDER[idx] });
    };

    const setActiveLayerByDigit = (digit: string) => {
      const state = sceneStore.getState();
      const target =
        digit === "1" && state.isDm ? "dm" :
        digit === "2" ? "shared" :
        digit === "3" ? "player" :
        null;
      if (!target) return;
      sceneStore.setToolOptions({ activeLayer: target });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcuts while the user is typing in an input somewhere.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
        return;
      }
      const mod = e.ctrlKey || e.metaKey;

      if (mod && !e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        sceneStore.undo();
        return;
      }
      if (mod && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) {
        e.preventDefault();
        sceneStore.redo();
        return;
      }
      if (mod && e.key.toLowerCase() === "a") {
        e.preventDefault();
        selectAllVisible();
        return;
      }
      if (mod && e.key.toLowerCase() === "d") {
        e.preventDefault();
        duplicateSelection();
        return;
      }
      if (e.key === "Escape") {
        getActiveTool().cancel?.();
        sceneStore.setSelected([]);
        scheduleFrame();
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        const ids = [...sceneStore.getState().selectedIds];
        if (ids.length > 0) {
          e.preventDefault();
          for (const id of ids) sceneStore.deleteLocal(id);
          sceneStore.setSelected([]);
        }
        return;
      }

      // Arrow-key nudge: 1 map unit, or 10 with shift for coarse moves.
      if (e.key.startsWith("Arrow")) {
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
        const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
        if (dx !== 0 || dy !== 0) {
          if (sceneStore.getState().selectedIds.size > 0) {
            e.preventDefault();
            nudgeSelection(dx, dy);
          }
        }
        return;
      }

      // Single-key shortcuts (no modifier).
      if (!mod && !e.altKey) {
        // Stroke width ladder.
        if (e.key === "[") {
          e.preventDefault();
          adjustStrokeWidth(-1);
          return;
        }
        if (e.key === "]") {
          e.preventDefault();
          adjustStrokeWidth(1);
          return;
        }
        // Active layer: 1=DM (DM only), 2=Shared, 3=Player.
        if (e.key === "1" || e.key === "2" || e.key === "3") {
          setActiveLayerByDigit(e.key);
          return;
        }
        // Tool shortcuts.
        const map: Record<string, ReturnType<typeof sceneStore.getState>["tool"]> = {
          v: "select",
          p: "pen",
          r: "rect",
          o: "ellipse",
          l: "line",
          a: "arrow",
        };
        const tool = map[e.key.toLowerCase()];
        if (tool) {
          getActiveTool().cancel?.();
          sceneStore.setTool(tool);
        }
      }
    };

    // ---------- wiring ----------

    liveC.addEventListener("pointerdown", onPointerDown);
    liveC.addEventListener("pointermove", onPointerMove);
    liveC.addEventListener("pointerup", onPointerUp);
    liveC.addEventListener("pointercancel", onPointerUp);
    liveC.addEventListener("wheel", onWheel, { passive: false });
    liveC.addEventListener("contextmenu", onContextMenu);
    liveC.addEventListener("dragover", onDragOver);
    liveC.addEventListener("drop", onDrop);
    window.addEventListener("keydown", onKeyDown);

    // Version-gated subscribe: only mark the static layer dirty when
    // the store actually reports a change that affects it. Tool and
    // tool-option changes still fire the subscription — we just
    // schedule a live-only frame for those.
    const onStoreNotify = () => {
      const v = sceneStore.getSceneVersion();
      if (v !== lastSceneVersionRef.current) {
        lastSceneVersionRef.current = v;
        markStaticDirty();
      } else {
        scheduleFrame();
      }
    };
    const unsubStore = sceneStore.subscribe(onStoreNotify);
    // Image cache notifies us when a deferred decode finishes — kick a
    // static repaint so the new bytes actually land on the canvas.
    const unsubImages = imageCache.subscribe(() => markStaticDirty());
    // Remote cursors + live strokes live on the live canvas only — they
    // don't touch the static layer, so just schedule a frame.
    const unsubOverlay = liveOverlay.subscribe(() => scheduleFrame());
    const ro = new ResizeObserver(resize);
    ro.observe(host);
    resize();

    return () => {
      ro.disconnect();
      unsubStore();
      unsubImages();
      unsubOverlay();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      liveC.removeEventListener("pointerdown", onPointerDown);
      liveC.removeEventListener("pointermove", onPointerMove);
      liveC.removeEventListener("pointerup", onPointerUp);
      liveC.removeEventListener("pointercancel", onPointerUp);
      liveC.removeEventListener("wheel", onWheel);
      liveC.removeEventListener("contextmenu", onContextMenu);
      liveC.removeEventListener("dragover", onDragOver);
      liveC.removeEventListener("drop", onDrop);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <div className="scene-canvas-host" ref={hostRef}>
      <canvas className="scene-canvas scene-canvas-static" ref={staticCanvasRef} />
      <canvas className="scene-canvas scene-canvas-live" ref={liveCanvasRef} />
    </div>
  );
}
